import React, { useEffect, useMemo } from 'react';
import {
  Box, List, useTheme, Link,
} from '@mui/material';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import CustomDrawer from '../../Drawer';
import NavigationItem from '../NavigationItem';
import { useHubPetParent } from './HubPetParentContext';
import { getNavItems } from './hubNavItems';
import getHubAudience from './hubAudience';
import { useMessagesUnread } from './Messages/MessagesUnreadContext';

type HubDrawerProps = {
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
  toggleDrawer: () => void;
  isDesktop: boolean;
};

export default function HubDrawer({
  drawerOpen, setDrawerOpen, toggleDrawer, isDesktop,
}: HubDrawerProps) {
  const petcademyTheme = useTheme();
  const location = useLocation();
  const [selected, setSelected] = React.useState(location.pathname);

  const { petParent } = useHubPetParent();
  const { unreadCount } = useMessagesUnread();

  const membershipExpired = petParent?.membershipStatus === 'expired';
  const audience = getHubAudience(petParent);

  const navItems = useMemo(
    () => getNavItems(membershipExpired, audience).filter((item) => item.showInDrawer && item.visible),
    [membershipExpired, audience],
  );

  useEffect(() => {
    setSelected(location.pathname);
  }, [location.pathname]);

  return (
    <CustomDrawer
      variant={isDesktop ? 'persistent' : 'temporary'}
      open={drawerOpen}
      desktop={isDesktop}
      onClose={toggleDrawer}
      onMouseEnter={() => isDesktop && setDrawerOpen(true)}
      onMouseLeave={() => isDesktop && setDrawerOpen(false)}
      inverted
    >
      <Box
        component={RouterLink}
        to="/hub/home"
        sx={{
          pt: {
            xs: 5,
            md: 4.5,
          },
          pb: 2,
          pl: 2,
          pr: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 0.6,
          textDecoration: 'none',
          cursor: 'pointer',
          '&:hover': {
            opacity: 0.8,
          },
        }}
      >
        {
          isDesktop && (
            <Box>
              <img style={{ width: '25px' }} alt="Petcademy Logo Icon" src="/hub/logo-icon-inverted.svg" />
            </Box>
          )
        }
        <Box sx={{ opacity: drawerOpen ? 1 : 0, transition: 'opacity 300ms', lineHeight: 0 }}>
          <img style={{ height: '20px' }} alt="Petcademy Logo Text" src="/hub/logo-text-inverted.svg" />
        </Box>
      </Box>
      <Box
        sx={{
          overflowY: drawerOpen ? 'auto' : 'hidden',
          overflowX: 'hidden',
          flexGrow: 1,
          '&::-webkit-scrollbar': {
            width: '5px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: petcademyTheme.palette.primary.light,
          },
        }}
      >
        <List>
          {
            navItems.map(
              (nav) => (
                <NavigationItem
                  {...nav}
                  selected={selected}
                  key={nav.name}
                  drawerOpen={drawerOpen}
                  badgeCount={nav.name === 'hub/messages' ? unreadCount : 0}
                  inverted
                />
              ),
            )
          }
        </List>
      </Box>
      {
        drawerOpen && (
          <Box
            sx={{
              color: 'primary.200',
              px: 2,
              pt: 1,
              pb: 5,
              fontSize: '0.8rem',
            }}
          >
            <Link
              href="https://petcademy.org/privacy-policy/"
              underline="hover"
              color="secondary.text"
              rel="noopener"
              target="_blank"
            >
              Privacy Policy
            </Link>
            {' '}
            •
            {' '}
            <Link
              href="https://petcademy.org/terms-and-conditions/"
              underline="hover"
              color="secondary.text"
              rel="noopener"
              target="_blank"
            >
              Terms
            </Link>
            {' '}
            •
            {' '}
            <Link
              href="https://petcademy.org/disclaimer/"
              underline="hover"
              color="secondary.text"
              rel="noopener"
              target="_blank"
            >
              Disclaimer
            </Link>
          </Box>
        )
      }
    </CustomDrawer>
  );
}
